import { useCallback } from "react";
import useSWR from "swr";
import { FileInfo, Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { FineAttachment, RenameFineAttachment } from "./types";

export const useFineAttachmentsApi = () => {
  const api = useApi();

  const useFineAttachments = useCallback(
    (query: PaginationQuery, signal: AbortSignal, fineId: string) => {
      const url = buildUrl(`/admin/fines/${fineId}/attachments`, query);
      return useSWR<Paginated<FineAttachment>, CustomError, string>(url, (url) =>
        api.get<Paginated<FineAttachment>>({ url, signal }),
      );
    },
    [api],
  );

  const deleteFineAttachment = useCallback(
    async (documentId: string, fineId: string): Promise<void> => {
      const url = `/admin/fine_attachments/${documentId}`;
      await api.del(url);
      api.mutateCache([`/admin/fines/${fineId}/attachments`]);
    },
    [api],
  );

  const insertFineAttachment = useCallback(
    async (file: File, overWrite?: boolean, fineId?: string): Promise<void> => {
      const formData = new FormData();
      formData.append("file", file, file.name);
      await api.put<FormData>(`/admin/fines/${fineId}/attachments${overWrite ? "?overwrite=1" : ""}`, formData);
      api.mutateCache([`/admin/fines/${fineId}/attachments`]);
    },
    [api],
  );

  const renameFineAttachment = useCallback(
    async (docId: string, body: RenameFineAttachment, fineId?: string): Promise<void> => {
      await api.put<RenameFineAttachment>(`/admin/fine_attachments/${docId}`, body);
      api.mutateCache([`/admin/fines/${fineId}/attachments`]);
    },
    [api],
  );

  const getFineAttachment = useCallback(
    (docID: string, signal: AbortSignal) => {
      const url = `/admin/fine_attachments/${docID}`;
      return api.get<FileInfo>({ url, signal });
    },
    [api],
  );

  return {
    useFineAttachments,
    deleteFineAttachment,
    insertFineAttachment,
    renameFineAttachment,
    getFineAttachment,
  };
};
